import { Button } from "@/components/ui/button"
import { Switch } from "@/components/ui/switch"
import { Input } from "@/components/ui/input"
import { Label } from "@/components/ui/label"
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card"
import { MessageSquare, Clock, TrendingUp, Coffee, Users, CheckCircle, BotIcon as Robot } from "lucide-react"
import { Image } from "@chakra-ui/react"
import { useTranslations } from "next-intl"
import Head from "next/head"
import HrefLang from "@/components/HrefLang"
import { useRouter } from "next/router"

export default function Home() {
    const t = useTranslations("ThreadsKeywordAutoReply")
    const title = t("Meta.title")
    const description = t("Meta.description")
    const router = useRouter()

    const handleUseFree = () => {
        router.push("/signup")
    }

    return (
        <div className="flex min-h-screen flex-col">
            <Head>
                <title>{title}</title>
                <meta name="og:title" content={title} />
                <meta name="og:description" content={description} />
                <meta name="description" content={description} />
            </Head>
            <HrefLang />
            <main className="flex-1">
                <section className="w-full py-12 md:py-24 lg:py-32 bg-gradient-to-b from-muted/50 to-background">
                    <div className="container px-4 md:px-6">
                        <div className="grid gap-6 lg:grid-cols-2 lg:gap-12 items-center">
                            <div className="flex flex-col justify-center space-y-4">
                                <div className="space-y-2">
                                    <h1 className="text-3xl font-bold tracking-tighter sm:text-5xl xl:text-6xl/none">
                                        {t("Title.keywordAutoReply")} <span className="text-primary">{t("Title.forThreads")}</span>
                                    </h1>
                                    <p className="max-w-[600px] text-muted-foreground md:text-xl">
                                        {t("Description.botHandleResponses")}
                                    </p>
                                </div>
                                <div className="flex flex-col gap-2 min-[400px]:flex-row">
                                    <Button size="lg" className="gap-2" onClick={handleUseFree}>
                                        <Robot className="w-4 h-4" />
                                        {t("Action.useFree")}
                                    </Button>
                                </div>
                            </div>
                            <div className="mx-auto w-full max-w-[500px] lg:max-w-none relative">
                                <div className="relative">
                                    <Image
                                        src="../keyword-auto-reply-landing-page.png"
                                        alt={t("Description.illustrationAlt")}
                                        width={384}
                                        height={384}
                                        className="w-full h-auto object-contain drop-shadow-md"
                                        priority
                                    />
                                </div>
                            </div>
                        </div>
                    </div>
                </section>

                <section className="w-full py-12 md:py-24 lg:py-32">
                    <div className="container px-4 md:px-6">
                        <div className="flex flex-col items-center justify-center space-y-4 text-center">
                            <div className="space-y-2">
                                <h2 className="text-3xl font-bold tracking-tighter md:text-4xl/tight">{t("Title.yourTimeIsValuable")}</h2>
                                <p className="max-w-[900px] text-muted-foreground md:text-xl/relaxed lg:text-base/relaxed xl:text-xl/relaxed">
                                    {t("Description.seeWhatYouCouldDo")}
                                </p>
                            </div>
                        </div>
                        <div className="mx-auto grid max-w-5xl grid-cols-1 gap-6 md:grid-cols-3 lg:gap-12 mt-12">
                            <Card className="relative overflow-hidden">
                                <div className="absolute top-0 right-0 p-3">
                                    <Coffee className="h-6 w-6 text-primary" />
                                </div>
                                <CardHeader>
                                    <CardTitle className="flex items-center gap-2">
                                        <span className="text-2xl font-bold">40</span>
                                        <span className="text-lg">{t("Title.hoursPerMonth")}</span>
                                    </CardTitle>
                                    <CardDescription>{t("Description.averageTimeSaved")}</CardDescription>
                                </CardHeader>
                                <CardContent>
                                    <p className="text-sm text-muted-foreground">{t("Message.extraWorkWeek")}</p>
                                </CardContent>
                            </Card>
                            <Card className="relative overflow-hidden">
                                <div className="absolute top-0 right-0 p-3">
                                    <MessageSquare className="h-6 w-6 text-primary" />
                                </div>
                                <CardHeader>
                                    <CardTitle className="flex items-center gap-2">
                                        <span className="text-2xl font-bold">1000+</span>
                                    </CardTitle>
                                    <CardDescription>{t("Description.autoRepliesPerMonth")}</CardDescription>
                                </CardHeader>
                                <CardContent>
                                    <p className="text-sm text-muted-foreground">
                                        {t("Message.letBotHandleResponses")}
                                    </p>
                                </CardContent>
                            </Card>
                            <Card className="relative overflow-hidden">
                                <div className="absolute top-0 right-0 p-3">
                                    <Users className="h-6 w-6 text-primary" />
                                </div>
                                <CardHeader>
                                    <CardTitle className="flex items-center gap-2">
                                        <span className="text-2xl font-bold">24/7</span>
                                    </CardTitle>
                                    <CardDescription>{t("Description.alwaysOnAvailability")}</CardDescription>
                                </CardHeader>
                                <CardContent>
                                    <p className="text-sm text-muted-foreground">
                                        {t("Message.botNeverSleeps")}
                                    </p>
                                </CardContent>
                            </Card>
                        </div>
                    </div>
                </section>

                <section className="w-full py-12 md:py-24 lg:py-32 bg-muted/50">
                    <div className="container px-4 md:px-6">
                        <div className="grid gap-6 lg:grid-cols-2 lg:gap-12 items-center">
                            <div className="flex flex-col justify-center space-y-4">
                                <div className="space-y-2">
                                    <h2 className="text-3xl font-bold tracking-tighter md:text-4xl/tight">
                                        {t("Title.whatCouldYouDoWithExtraTime")}
                                    </h2>
                                    <p className="text-muted-foreground md:text-xl">
                                        {t("Description.whileBotHandlesResponses")}
                                    </p>
                                </div>
                                <div className="space-y-4">
                                    <div className="flex items-center gap-4">
                                        <CheckCircle className="h-6 w-6 text-primary" />
                                        <p className="text-lg">{t("Message.growingYourBusiness")}</p>
                                    </div>
                                    <div className="flex items-center gap-4">
                                        <CheckCircle className="h-6 w-6 text-primary" />
                                        <p className="text-lg">{t("Message.creatingBetterContent")}</p>
                                    </div>
                                    <div className="flex items-center gap-4">
                                        <CheckCircle className="h-6 w-6 text-primary" />
                                        <p className="text-lg">{t("Message.engagingWithCommunity")}</p>
                                    </div>
                                    <div className="flex items-center gap-4">
                                        <CheckCircle className="h-6 w-6 text-primary" />
                                        <p className="text-lg">{t("Message.takingBreaks")}</p>
                                    </div>
                                </div>
                            </div>
                            <div className="rounded-xl border bg-background p-6 shadow-lg space-y-4">
                                <div className="space-y-2">
                                    <h3 className="text-2xl font-bold">{t("Title.potentialTimeSavings")}</h3>
                                    <p className="text-muted-foreground">{t("Description.basedOnAverageResponseTime")}</p>
                                </div>
                                <div className="grid gap-4">
                                    <Card>
                                        <CardContent className="pt-6">
                                            <div className="flex justify-between items-center">
                                                <div>
                                                    <p className="text-sm font-medium">{t("Title.dailyMessages")}</p>
                                                    <p className="text-2xl font-bold">30</p>
                                                </div>
                                                <div>
                                                    <p className="text-sm font-medium">{t("Title.timeSaved")}</p>
                                                    <p className="text-2xl font-bold text-primary">{t("Message.oneHour")}</p>
                                                </div>
                                            </div>
                                        </CardContent>
                                    </Card>
                                    <Card>
                                        <CardContent className="pt-6">
                                            <div className="flex justify-between items-center">
                                                <div>
                                                    <p className="text-sm font-medium">{t("Title.monthlyMessages")}</p>
                                                    <p className="text-2xl font-bold">900</p>
                                                </div>
                                                <div>
                                                    <p className="text-sm font-medium">{t("Title.timeSaved")}</p>
                                                    <p className="text-2xl font-bold text-primary">{t("Message.thirtyHours")}</p>
                                                </div>
                                            </div>
                                        </CardContent>
                                    </Card>
                                </div>
                            </div>
                        </div>
                    </div>
                </section>

                <section id="features" className="w-full py-12 md:py-24 lg:py-32">
                    <div className="container px-4 md:px-6">
                        <div className="flex flex-col items-center justify-center space-y-4 text-center">
                            <div className="space-y-2">
                                <h2 className="text-3xl font-bold tracking-tighter md:text-4xl/tight">{t("Title.powerfulFeatures")}</h2>
                                <p className="max-w-[900px] text-muted-foreground md:text-xl/relaxed lg:text-base/relaxed xl:text-xl/relaxed">
                                    {t("Description.keywordAutoReplyFeatures")}
                                </p>
                            </div>
                        </div>
                        <div className="mx-auto grid max-w-5xl grid-cols-1 gap-6 md:grid-cols-3 lg:gap-12 mt-8">
                            <Card>
                                <CardHeader className="pb-2">
                                    <MessageSquare className="h-6 w-6 mb-2 text-primary" />
                                    <CardTitle>{t("Title.smartDetection")}</CardTitle>
                                </CardHeader>
                                <CardContent>
                                    <p className="text-sm text-muted-foreground">
                                        {t("Description.automaticallyDetectsMessages")}
                                    </p>
                                </CardContent>
                            </Card>
                            <Card>
                                <CardHeader className="pb-2">
                                    <Clock className="h-6 w-6 mb-2 text-primary" />
                                    <CardTitle>{t("Title.timeSaving")}</CardTitle>
                                </CardHeader>
                                <CardContent>
                                    <p className="text-sm text-muted-foreground">
                                        {t("Description.saveHoursByAutomating")}
                                    </p>
                                </CardContent>
                            </Card>
                            <Card>
                                <CardHeader className="pb-2">
                                    <TrendingUp className="h-6 w-6 mb-2 text-primary" />
                                    <CardTitle>{t("Title.performanceAnalytics")}</CardTitle>
                                </CardHeader>
                                <CardContent>
                                    <p className="text-sm text-muted-foreground">
                                        {t("Description.trackMessagesWithStatistics")}
                                    </p>
                                </CardContent>
                            </Card>
                        </div>
                    </div>
                </section>

                <section id="how-it-works" className="w-full py-12 md:py-24 lg:py-32 bg-muted/50">
                    <div className="container px-4 md:px-6">
                        <div className="flex flex-col items-center justify-center space-y-4 text-center">
                            <div className="space-y-2">
                                <h2 className="text-3xl font-bold tracking-tighter md:text-4xl/tight">{t("Title.howItWorks")}</h2>
                                <p className="max-w-[900px] text-muted-foreground md:text-xl/relaxed lg:text-base/relaxed xl:text-xl/relaxed">
                                    {t("Description.settingUpIsSimple")}
                                </p>
                            </div>
                        </div>
                        <div className="mx-auto grid max-w-5xl grid-cols-1 gap-8 md:grid-cols-2 lg:gap-12 items-center mt-12">
                            <div className="space-y-4">
                                <div className="flex items-center gap-4">
                                    <div className="flex h-10 w-10 items-center justify-center rounded-full bg-primary text-primary-foreground">
                                        1
                                    </div>
                                    <h3 className="text-xl font-bold">{t("Title.enableAutoReply")}</h3>
                                </div>
                                <p className="text-muted-foreground">
                                    {t("Description.turnOnAutoReply")}
                                </p>
                                <div className="flex items-center gap-4">
                                    <div className="flex h-10 w-10 items-center justify-center rounded-full bg-primary text-primary-foreground">
                                        2
                                    </div>
                                    <h3 className="text-xl font-bold">{t("Title.addYourKeywords")}</h3>
                                </div>
                                <p className="text-muted-foreground">
                                    {t("Description.enterKeywords")}
                                </p>
                                <div className="flex items-center gap-4">
                                    <div className="flex h-10 w-10 items-center justify-center rounded-full bg-primary text-primary-foreground">
                                        3
                                    </div>
                                    <h3 className="text-xl font-bold">{t("Title.setYourResponses")}</h3>
                                </div>
                                <p className="text-muted-foreground">
                                    {t("Description.customizeAutoReplies")}
                                </p>
                            </div>
                            <div className="rounded-xl border bg-background p-6 shadow-lg">
                                <div className="space-y-4">
                                    <div className="space-y-2">
                                        <h4 className="text-xl font-bold">{t("Title.keywordAutoReplySettings")}</h4>
                                        <p className="text-sm text-muted-foreground">{t("Description.detectAndAutoReply")}</p>
                                    </div>
                                    <div className="space-y-4">
                                        <div className="flex items-center justify-between">
                                            <div>
                                                <h5 className="font-medium">{t("Title.autoReply")}</h5>
                                                <p className="text-xs text-muted-foreground">{t("Hint.autoReplyWhenEnabled")}</p>
                                            </div>
                                            <Switch />
                                        </div>
                                        <div className="space-y-2">
                                            <Label htmlFor="keywords">{t("Title.keywords")}</Label>
                                            <Input id="keywords" placeholder={t("Hint.keywordsToDetect")} />
                                        </div>
                                        <div className="space-y-2">
                                            <div className="flex items-center justify-between">
                                                <Label htmlFor="account">{t("Title.threadsAccount")}</Label>
                                                <span className="text-sm">threadslytics</span>
                                            </div>
                                        </div>
                                        <Button className="w-full" variant="outline">
                                            {t("Action.save")}
                                        </Button>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                </section>

                <section className="w-full py-12 md:py-24 lg:py-32 bg-primary text-primary-foreground mb-12">
                    <div className="container px-4 md:px-6">
                        <div className="flex flex-col items-center justify-center space-y-4 text-center">
                            <div className="space-y-2">
                                <h2 className="text-3xl font-bold tracking-tighter md:text-4xl/tight">{t("Title.startSavingTimeToday")}</h2>
                                <p className="max-w-[600px] md:text-xl/relaxed lg:text-base/relaxed xl:text-xl/relaxed">
                                    {t("Description.letBotHandleWhileYouFocus")}
                                </p>
                            </div>
                            <Button size="lg" variant="secondary" className="mt-4" onClick={handleUseFree}>
                                <Robot className="w-4 h-4 mr-2" />
                                {t("Action.useFree")}
                            </Button>
                        </div>
                    </div>
                </section>
            </main>
        </div>
    )
}

export async function getServerSideProps(context) {
    return {
        props: {
            messages: (await import(`@/messages/${context.locale}.json`)).default,
        },
    }
}